/**
 * Context Update Factories
 *
 * Pure functions that return partial context objects for use with XState's assign().
 * These are NOT XState actions themselves - they're utilities that create the object
 * you pass to assign().
 *
 * Example usage:
 *   actions: assign(createClearedPaymentContext())
 *   actions: assign(({ context, event }) => createSpecificTreatmentContext(event.data, context.hrts))
 */

import { type HrtType, resolveSelectedHrt } from "./hrt-selection";
import {
  type PricingMap,
  shouldUseInsuredPricing,
  updatePricingMapMode,
} from "./pricing-mode";

/**
 * Context properties that should be cleared when resetting payment state.
 */
export const PAYMENT_CONTEXT_KEYS = [
  "paymentStatus",
  "posError",
  "paymentError",
  "paymentIntentId",
  "setupIntentId",
  "clientSecret",
  "amount",
  "cardAuthRecorded",
  "cardAuthRecording",
] as const;

/**
 * Creates a partial context object that clears all payment-related state.
 * Used when navigating back from payment states.
 */
export function createClearedPaymentContext(): Record<string, undefined> {
  return {
    paymentStatus: undefined,
    posError: undefined,
    paymentError: undefined,
    paymentIntentId: undefined,
    setupIntentId: undefined,
    clientSecret: undefined,
    amount: undefined,
    cardAuthRecorded: undefined,
    cardAuthRecording: undefined,
  };
}

/**
 * Creates a partial context object that clears payment and saved card state.
 * Used when user explicitly chooses to use a new card.
 */
export function createClearedPaymentAndCardContext(): Record<string, unknown> {
  return {
    ...createClearedPaymentContext(),
    savedPaymentMethod: null,
  };
}

/**
 * Creates the context update for a specific treatment selection.
 * Resolves the HRT from event data and sets routing context.
 */
export function createSpecificTreatmentContext(
  eventData: Record<string, unknown> | undefined,
  hrts: HrtType[] | undefined,
): Record<string, unknown> {
  const { selectedHrt } = resolveSelectedHrt(
    eventData as { hrtIds?: number[] } | undefined,
    hrts,
  );

  return {
    ...(eventData || {}),
    selectedHrt,
    targetTreatmentState: "specificTreatment",
    preDataWriteState: "storefrontBrowse",
  };
}

/**
 * Result type for default treatment context update
 */
export interface DefaultTreatmentContextUpdate {
  preBrowseChoice: "defaultTreatment";
  showPreBrowseModal: false;
  targetTreatmentState: "defaultTreatment";
  preDataWriteState: "storefrontBrowse";
  selectedHrt?: undefined;
  pricingMap?: PricingMap;
  [key: string]: unknown;
}

/**
 * Creates the context update for selecting the default (urgent care) treatment.
 * Handles the common pattern of updating pricing mode to INSURED when applicable.
 */
export function createDefaultTreatmentContextUpdate(context: {
  defaultHrt?: { hrtId: number };
  pricingMap?: Record<string, unknown>;
  allActive?: boolean;
  insuranceStates?: { primary?: { active?: boolean } };
  pendingPricesMode?: boolean;
}): DefaultTreatmentContextUpdate {
  const baseUpdate: DefaultTreatmentContextUpdate = {
    preBrowseChoice: "defaultTreatment",
    showPreBrowseModal: false,
    targetTreatmentState: "defaultTreatment",
    preDataWriteState: "storefrontBrowse",
    selectedHrt: undefined,
    isWorkersComp: undefined,
    isWorkplaceInjury: undefined,
  };

  const defaultHrt = context.defaultHrt;
  const pricingMap = context.pricingMap as PricingMap | undefined;

  if (!defaultHrt || !shouldUseInsuredPricing(context)) {
    return baseUpdate;
  }

  const existingEntry = pricingMap?.[defaultHrt.hrtId];
  if (!existingEntry) {
    return baseUpdate;
  }

  // Only set INSURED mode if the pricing entry actually has INSURED price data.
  // When pendingPricesMode is true but insurance is unverified, the pricing API
  // may only return CASH pricing — forcing INSURED mode would cause a lookup miss
  // in the create-appointment actor (TC81).
  const priceData = existingEntry.priceData as
    | Record<string, unknown>
    | undefined;
  const hasInsuredPricing = priceData?.INSURED != null;

  if (!hasInsuredPricing) {
    return baseUpdate;
  }

  return {
    ...baseUpdate,
    pricingMap: updatePricingMapMode(pricingMap, defaultHrt.hrtId, "INSURED"),
  };
}
